/**
 * localStorage wrappers. All persistent state lives here so views never
 * touch `localStorage` directly. Keys + serialization shapes are documented
 * inline; if you change a shape, bump the `:v<N>` suffix in the key so old
 * data is discarded rather than crashing on a deserialize.
 */
import type { Locale, SavedSolution, ThemeMode } from './types';

export const SOLVED_KEY = 'doublet-play:solved:v2';
export const SOLUTIONS_KEY = 'doublet-play:solutions:v1';
export const PROGRESS_KEY = 'doublet-play:progress:v1';
export const WORDS_KEY = 'doublet-play:words:v1';
export const DEF_CACHE_KEY = 'doublet-play:defs:v4';
export const THEME_KEY = 'doublet-play:theme';
export const LOCALE_KEY = 'doublet-play:locale';
export const TUTORIAL_KEY = 'doublet-play:tutorialSeen';
export const UNLOCKED_KEY = 'doublet-play:unlocked';

const VALID_LOCALES: readonly Locale[] = ['en', 'fr', 'de'];

// ────────────────────────────────────────────────────────────────────────────
// Solved-set + saved solutions

export function getSolvedSet(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(SOLVED_KEY) ?? '[]'));
  } catch {
    return new Set();
  }
}

export function isSolved(locale: Locale, worldId: string, idx: number): boolean {
  return getSolvedSet().has(`${locale}|${worldId}|${idx}`);
}

export function markSolved(
  locale: Locale,
  worldId: string,
  idx: number,
  fullPath: readonly string[],
  hintsUsed: number
): void {
  // 1. Add to solved-key set
  const solved = getSolvedSet();
  solved.add(`${locale}|${worldId}|${idx}`);
  localStorage.setItem(SOLVED_KEY, JSON.stringify([...solved]));

  // 2. Save the intermediates + hint count
  if (fullPath.length >= 2) {
    const intermediates = fullPath.slice(1, -1);
    saveSolution(locale, worldId, idx, intermediates, hintsUsed);
  }

  // 3. Track unique words per locale (start + target + intermediates)
  let byLocale: Record<string, string[]> = {};
  try { byLocale = JSON.parse(localStorage.getItem(WORDS_KEY) ?? '{}'); } catch {}
  const set = new Set(byLocale[locale] ?? []);
  for (const w of fullPath) set.add(w);
  byLocale[locale] = [...set];
  localStorage.setItem(WORDS_KEY, JSON.stringify(byLocale));
}

export function saveSolution(
  locale: Locale,
  worldId: string,
  idx: number,
  intermediates: readonly string[],
  hintsUsed: number
): void {
  let by: Record<string, { intermediates: string[]; hintsUsed: number }> = {};
  try { by = JSON.parse(localStorage.getItem(SOLUTIONS_KEY) ?? '{}'); } catch {}
  by[`${locale}|${worldId}|${idx}`] = { intermediates: [...intermediates], hintsUsed };
  localStorage.setItem(SOLUTIONS_KEY, JSON.stringify(by));
}

export function getSavedSolution(
  locale: Locale,
  worldId: string,
  idx: number
): SavedSolution | null {
  try {
    const by = JSON.parse(localStorage.getItem(SOLUTIONS_KEY) ?? '{}');
    const got = by[`${locale}|${worldId}|${idx}`];
    if (!got) return null;
    // Legacy: bare array of intermediates with no hint count
    if (Array.isArray(got)) return { intermediates: got, hintsUsed: null };
    if (Array.isArray(got.intermediates)) {
      return {
        intermediates: got.intermediates,
        hintsUsed: typeof got.hintsUsed === 'number' ? got.hintsUsed : null,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function clearSolved(locale: Locale, worldId: string, idx: number): void {
  const solved = getSolvedSet();
  solved.delete(`${locale}|${worldId}|${idx}`);
  localStorage.setItem(SOLVED_KEY, JSON.stringify([...solved]));
  try {
    const by = JSON.parse(localStorage.getItem(SOLUTIONS_KEY) ?? '{}');
    delete by[`${locale}|${worldId}|${idx}`];
    localStorage.setItem(SOLUTIONS_KEY, JSON.stringify(by));
  } catch {}
}

// ────────────────────────────────────────────────────────────────────────────
// Partial progress

export function saveProgress(
  locale: Locale,
  worldId: string,
  idx: number,
  intermediates: readonly (string | null)[]
): void {
  // Empty progress (no slot filled) => clear instead of save
  if (!intermediates.some((w) => w)) {
    clearProgress(locale, worldId, idx);
    return;
  }
  let by: Record<string, (string | null)[]> = {};
  try { by = JSON.parse(localStorage.getItem(PROGRESS_KEY) ?? '{}'); } catch {}
  by[`${locale}|${worldId}|${idx}`] = [...intermediates];
  localStorage.setItem(PROGRESS_KEY, JSON.stringify(by));
}

export function getSavedProgress(
  locale: Locale,
  worldId: string,
  idx: number
): readonly (string | null)[] | null {
  try {
    const by = JSON.parse(localStorage.getItem(PROGRESS_KEY) ?? '{}');
    const got = by[`${locale}|${worldId}|${idx}`];
    return Array.isArray(got) ? got : null;
  } catch {
    return null;
  }
}

export function clearProgress(locale: Locale, worldId: string, idx: number): void {
  try {
    const by = JSON.parse(localStorage.getItem(PROGRESS_KEY) ?? '{}');
    delete by[`${locale}|${worldId}|${idx}`];
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(by));
  } catch {}
}

export function hasSavedProgress(locale: Locale, worldId: string, idx: number): boolean {
  const got = getSavedProgress(locale, worldId, idx);
  return Array.isArray(got) && got.some((w) => w);
}

// ────────────────────────────────────────────────────────────────────────────
// Learned words

export function getLearnedWordsByLocale(): Record<string, string[]> {
  try { return JSON.parse(localStorage.getItem(WORDS_KEY) ?? '{}'); }
  catch { return {}; }
}

// ────────────────────────────────────────────────────────────────────────────
// Locale + theme + tutorial + unlocked

export function getStoredLocale(): Locale | null {
  const v = localStorage.getItem(LOCALE_KEY);
  return v && (VALID_LOCALES as readonly string[]).includes(v) ? (v as Locale) : null;
}
export function setStoredLocale(locale: Locale): void {
  localStorage.setItem(LOCALE_KEY, locale);
}

export function getStoredTheme(): ThemeMode {
  const v = localStorage.getItem(THEME_KEY);
  return v === 'light' || v === 'dark' || v === 'auto' ? v : 'auto';
}
export function setStoredTheme(theme: ThemeMode): void {
  localStorage.setItem(THEME_KEY, theme);
}

export function hasSeenTutorial(): boolean {
  return localStorage.getItem(TUTORIAL_KEY) === '1';
}
export function markTutorialSeen(): void {
  localStorage.setItem(TUTORIAL_KEY, '1');
}

export function isUnlocked(): boolean {
  return localStorage.getItem(UNLOCKED_KEY) === '1';
}
export function setUnlocked(v: boolean): void {
  localStorage.setItem(UNLOCKED_KEY, v ? '1' : '0');
}

// ────────────────────────────────────────────────────────────────────────────
// Reset all progress (settings drawer button)

export function resetAllProgress(): void {
  localStorage.removeItem(SOLVED_KEY);
  localStorage.removeItem(SOLUTIONS_KEY);
  localStorage.removeItem(PROGRESS_KEY);
  localStorage.removeItem(WORDS_KEY);
}
